/**
 * GlobalImpactView — world-scale view of avoided impacts.
 *
 * This is the "climate + resource depletion + stratospheric ozone" tab.
 * It is rendered outside the Leaflet MapContainer because it uses MapLibre
 * GL (globe projection) instead of Leaflet, so the parent card treats the
 * Global tab as a fully self-contained subtree rather than a layer.
 *
 * Layout (rendered by GeneratorAttributionCard on Global tab):
 *   [ GlobalMetricLegend — endpoint mix bar ]
 *   [ GlobalImpactGlobe    — MapLibre globe with plant markers ]
 *
 * NOTE: the old cars-off-the-road framing lived here and has been removed.
 * The three-endpoint headline now lives at the top of
 * GeneratorAttributionCard and applies to all three tabs.
 */

import { useEffect, useMemo, useRef } from "react";
import maplibregl, { Map as MLMap } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import type { DisplacedGeneratorEnriched } from "../../model/domain/types";
import {
  fmtMetric,
  getFuelColor,
  getFuelDisplayName,
} from "../helpers";

// ──────────────────────────────────────────────────────────────────────
// Global-scale endpoint aggregation (the share-bar shown above the globe)
// ──────────────────────────────────────────────────────────────────────

interface GlobalBucket {
  label: string;
  value: number; // DALYs
  color: string;
}

const GLOBAL_KEYS: Array<{ match: RegExp; label: string; color: string }> = [
  { match: /climate change/i, label: "Climate change", color: "#F59E0B" },
  { match: /ozone depletion/i, label: "Stratospheric ozone", color: "#8B5CF6" },
  { match: /ionising radiation/i, label: "Ionising radiation", color: "#EF4444" },
  { match: /natural resources|fossil|mineral/i, label: "Resource depletion", color: "#10B981" },
];

function aggregateGlobalEndpoints(
  plants: DisplacedGeneratorEnriched[],
): GlobalBucket[] {
  const totals = GLOBAL_KEYS.map((k) => ({ ...k, value: 0 })) as Array<
    { match: RegExp; label: string; color: string; value: number }
  >;

  for (const p of plants) {
    const impacts = p.impacts_by_category;
    if (!impacts) continue;
    for (const [key, val] of Object.entries(impacts)) {
      for (const bucket of totals) {
        if (
          bucket.match.test(key) &&
          (key.endsWith("_DALYs") || key.endsWith("_species.yr"))
        ) {
          bucket.value += Math.abs(val);
          break;
        }
      }
    }
  }

  return totals
    .filter((b) => b.value > 0)
    .map(({ label, value, color }) => ({ label, value, color }));
}

// ──────────────────────────────────────────────────────────────────────
// Endpoint-mix legend (rendered above the globe by the parent card)
// ──────────────────────────────────────────────────────────────────────

export function GlobalMetricLegend({
  plants,
}: {
  plants: DisplacedGeneratorEnriched[];
}) {
  const buckets = aggregateGlobalEndpoints(plants);
  const totalDalys = buckets.reduce((s, b) => s + b.value, 0);
  if (buckets.length === 0 || totalDalys <= 0) return null;

  return (
    <div className="mb-3 rounded-lg bg-surface-600/50 border border-border/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">
        Global-scale endpoint mix (DALYs)
      </div>
      <div className="space-y-1.5">
        {buckets.map((b) => {
          const pct = (b.value / totalDalys) * 100;
          return (
            <div key={b.label} className="flex items-center gap-2 text-[11px]">
              <span className="w-40 shrink-0 text-text-secondary">{b.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-surface-700 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: b.color }}
                />
              </div>
              <span className="w-20 text-right tabular-nums text-text-primary">
                {fmtMetric(b.value, "DALYs")}
              </span>
              <span className="w-10 text-right tabular-nums text-text-muted">
                {pct.toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-text-muted mt-2 leading-tight">
        Atmospheric mixing means these impacts are felt worldwide,
        regardless of where the plant is located.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// MapLibre GL globe — replaces the Leaflet Global-tab layer
// ──────────────────────────────────────────────────────────────────────

interface GlobeProps {
  plants: DisplacedGeneratorEnriched[];
  projectLatitude?: number;
  projectLongitude?: number;
  heightClass?: string; // "h-[480px]" etc.
}

/**
 * Minimal OSM-based raster style — no Mapbox token required.
 * We use a dark Carto basemap to match the Leaflet tabs' visual style.
 */
const RASTER_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    carto: {
      type: "raster",
      tiles: [
        "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
      ],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors © CARTO",
    },
  },
  layers: [
    { id: "carto-dark", type: "raster", source: "carto" },
  ],
};

// Fixed global-climate sprinkle pattern. A deterministic seed gives the
// same dot set on every render so it reads as "the climate is everywhere"
// rather than a jittery random layer.
const CLIMATE_DOT_COUNT = 360;
const CLIMATE_DOTS: Array<{ lat: number; lng: number }> = (() => {
  let s = 0x9e3779b9;
  const rnd = () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return ((s >>> 0) % 1_000_000) / 1_000_000;
  };
  const out: Array<{ lat: number; lng: number }> = [];
  for (let i = 0; i < CLIMATE_DOT_COUNT; i++) {
    // Equal-area sampling on the sphere so the pole regions don't cluster.
    const u = rnd();
    const v = rnd();
    const lat = Math.acos(2 * u - 1) * (180 / Math.PI) - 90;
    const lng = v * 360 - 180;
    out.push({ lat, lng });
  }
  return out;
})();

const CLIMATE_DOT_STYLE_ID = "tv-climate-dot-style";
function ensureClimateDotStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CLIMATE_DOT_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = CLIMATE_DOT_STYLE_ID;
  el.textContent = `
.tv-climate-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #F59E0B;
  opacity: 0.65;
  box-shadow: 0 0 4px rgba(245, 158, 11, 0.55);
  pointer-events: none;
}`;
  document.head.appendChild(el);
}

export function GlobalImpactGlobe({
  plants,
  projectLatitude,
  projectLongitude,
  heightClass = "h-[480px]",
}: GlobeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const climateDotsRef = useRef<maplibregl.Marker[]>([]);
  const rotationRef = useRef<number | null>(null);

  const { mappable, maxImpact } = useMemo(() => {
    const list = plants.filter((p) => p.latitude && p.longitude);
    const max = list.reduce(
      (acc, p) => Math.max(acc, Math.abs(p.dalys ?? 0)),
      0,
    );
    return { mappable: list, maxImpact: max };
  }, [plants]);

  // ── init map ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: RASTER_STYLE,
      center: [-40, 35],
      zoom: 1.4,
      pitch: 0,
      attributionControl: { compact: true },
    });
    // MapLibre GL 5.x supports the globe projection + an atmospheric
    // sky ring that wraps the sphere — the visual that ties the globe
    // to the "climate is a global commons" story. Both are guarded so
    // older builds silently fall back to a flat mercator world.
    map.on("load", () => {
      try {
        if (typeof map.setProjection === "function") {
          map.setProjection({ type: "globe" });
        }
      } catch {
        // no-op
      }
      try {
        // Atmospheric halo. Horizon blends into a thin hazy layer, space
        // stays near-black. Keep fog subtle so continents don't wash out.
        (map as unknown as {
          setSky?: (opts: Record<string, unknown>) => void;
        }).setSky?.({
          "sky-color": "#0a0f1e",
          "horizon-color": "#4b6ea8",
          "fog-color": "#0a0f1e",
          "sky-horizon-blend": 0.55,
          "horizon-fog-blend": 0.45,
          "fog-ground-blend": 0.1,
          "atmosphere-blend": 0.85,
        });
      } catch {
        // no-op
      }
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    // Climate-change sprinkle: small pulsing amber dots across the whole
    // globe. Communicates "these impacts are not local — CO2 travels".
    ensureClimateDotStyles();
    for (const d of CLIMATE_DOTS) {
      const el = document.createElement("div");
      el.className = "tv-climate-dot";
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([d.lng, d.lat])
        .addTo(map);
      climateDotsRef.current.push(marker);
    }

    // Slow auto-rotation when idle (~20s per full rotation).
    let lastT = performance.now();
    const tick = (t: number) => {
      if (!mapRef.current) return;
      const dt = t - lastT;
      lastT = t;
      const m = mapRef.current;
      // Stop rotating if user is interacting (isMoving true).
      if (!m.isMoving()) {
        const speedDegPerMs = 360 / 20_000;
        const center = m.getCenter();
        m.setCenter([center.lng + speedDegPerMs * dt, center.lat]);
      }
      rotationRef.current = requestAnimationFrame(tick);
    };
    rotationRef.current = requestAnimationFrame(tick);

    return () => {
      if (rotationRef.current) cancelAnimationFrame(rotationRef.current);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      climateDotsRef.current.forEach((m) => m.remove());
      climateDotsRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // ── (re)render plant markers when data changes ──────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // clear previous markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    mappable.forEach((p) => {
      const impact = Math.abs(p.dalys ?? 0);
      const t = maxImpact > 0 ? Math.sqrt(impact / maxImpact) : 0;
      const size = Math.max(6, Math.min(22, 6 + t * 16));
      const el = document.createElement("div");
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.borderRadius = "50%";
      el.style.backgroundColor = getFuelColor(p.fuel_type);
      el.style.opacity = "0.7";
      el.style.border = "1px solid #ffffff";
      el.style.boxShadow = "0 0 6px rgba(0,0,0,0.4)";
      el.style.cursor = "pointer";

      const popup = new maplibregl.Popup({
        offset: 10,
        closeButton: true,
      }).setHTML(`
        <div style="font-family:inherit;min-width:200px">
          <div style="font-weight:600;color:#111827;margin-bottom:2px">${escapeHtml(p.plant_name)}</div>
          <div style="color:#4B5563;font-size:11px">
            ${escapeHtml(getFuelDisplayName(p.fuel_type))} · ${escapeHtml(p.state || "")}
          </div>
          <div style="color:#111827;font-size:12px;margin-top:6px">
            <strong>${fmtMetric(impact, "DALYs")}</strong> DALYs avoided
          </div>
        </div>
      `);

      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([p.longitude, p.latitude])
        .setPopup(popup)
        .addTo(map);
      markersRef.current.push(marker);
    });

    // Project location marker — distinctive cyan diamond
    if (
      projectLatitude != null &&
      projectLongitude != null &&
      projectLatitude !== 0 &&
      projectLongitude !== 0
    ) {
      const el = document.createElement("div");
      el.style.width = "14px";
      el.style.height = "14px";
      el.style.backgroundColor = "#00e5ff";
      el.style.border = "2px solid #ffffff";
      el.style.transform = "rotate(45deg)";
      el.style.boxShadow = "0 0 8px rgba(0,229,255,0.6)";
      el.style.cursor = "pointer";
      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([projectLongitude, projectLatitude])
        .setPopup(
          new maplibregl.Popup({ offset: 12 }).setHTML(`
            <div style="font-family:inherit;min-width:200px">
              <div style="font-weight:600;color:#111827">Your project site</div>
              <div style="color:#4B5563;font-size:11px;margin-top:2px">
                Climate + resource benefits from displacement are felt
                worldwide, regardless of where the plant is located.
              </div>
            </div>
          `),
        )
        .addTo(map);
      markersRef.current.push(marker);
    }
  }, [mappable, maxImpact, projectLatitude, projectLongitude]);

  return (
    <div
      className={`rounded-lg border border-border overflow-hidden w-full ${heightClass}`}
    >
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
